import { ComponentProps, useCallback } from 'react';

import { CrowdfundingProvider, useNavigateToCampaign } from '#Context/index.js';
import { CampaignCard } from '#crowdfunding/index.js';
import { CampaignStoreItem, CampaignStoreItems } from '@givebacks/api/store';
import { useBreakPoints } from 'core';
import { Grid } from 'ui';

type CampaignCardListProps = {
  data: CampaignStoreItems;
  causeId?: string;

  /**
   * If true, the campaigns will be stacked in a single column
   */
  isStacked?: boolean;

  /**
   * If true, the campaigns will be displayed as dumb cards, instead of loading its own data
   */
  isDumbCard?: boolean;

  /**
   * Props passed to the underlying CampaignCard component
   */
  campaignCardProps?: Partial<ComponentProps<typeof CampaignCard>>;

  /**
   * If provided, will override the default navigation behavior
   */
  navigateToCampaign?: (navigateToCampaign: (to: string) => void, campaignId: string) => void;
};

const CampaignCardList = ({
  data,
  causeId,
  isStacked,
  isDumbCard,
  campaignCardProps,
  navigateToCampaign,
}: CampaignCardListProps) => {
  const { isSmallScreen } = useBreakPoints();

  const _navigateToCampaign = useNavigateToCampaign();

  const onCampaignCardClick = useCallback(
    (campaign: CampaignStoreItem) => {
      if (navigateToCampaign) {
        // if a custom navigateToCampaign has been passed in, use it
        // Call the custom navigateToCampaign, passing back in a convenient function that already has the campaign.
        navigateToCampaign?.(
          (to: string) => {
            _navigateToCampaign(campaign, to);
          },
          campaign?.uuid
        );

        return;
      }

      if (campaign.__type === 'event') {
        _navigateToCampaign(campaign, `events/${campaign?.uuid}`);
      } else {
        _navigateToCampaign(campaign, `campaigns/${campaign?.uuid}`);
      }
    },
    [navigateToCampaign, _navigateToCampaign]
  );

  return (
    <Grid>
      {data?.map((campaign) => {
        return (
          <Grid.Col span={{ base: 12, sm: isStacked ? 12 : 4 }} key={campaign.uuid}>
            <CrowdfundingProvider campaignId={campaign.uuid}>
              <CampaignCard
                campaign_id={isDumbCard ? undefined : campaign.uuid}
                onClick={() => onCampaignCardClick(campaign)}
                causeId={causeId}
                isColumn={!isSmallScreen}
                campaignData={isDumbCard ? campaign : undefined}
                {...campaignCardProps}
              />
            </CrowdfundingProvider>
          </Grid.Col>
        );
      })}
    </Grid>
  );
};

export { CampaignCardList };
